import type { OutletPaymentSales, PaymentSalesMode } from '@repo/desktop-bridge';

import { formatPaise } from '@/lib/money';

import { paymentModeLabel } from './payment-modes';
import { tillPlate } from './TillFrame';

/** A mode as a cashier says it; `UNKNOWN` is the till's own, not a way to pay. */
const modeLabel = (mode: PaymentSalesMode): string =>
  mode === 'UNKNOWN' ? 'Unknown' : paymentModeLabel(mode);

/**
 * One outlet's day by payment mode (#323): a row per mode — bills, taken,
 * handed back, net — and the total.
 *
 * Every figure is the main process's, in integer paise; nothing is added up
 * here. Cash, UPI and Card are always rows, at ₹0 where nothing was taken, so
 * a missing line never reads as a missing mode. Unknown appears only where the
 * till holds tickets from before it kept how they were paid.
 */
export function OutletPayments({
  sales,
  named,
}: {
  sales: OutletPaymentSales;
  /** Whether to head it with its outlet — only where more than one sold today. */
  named: boolean;
}) {
  return (
    <section className="space-y-4">
      {named && <h2 className={tillPlate}>{sales.outlet ?? sales.terminal}</h2>}

      <table className="w-full text-base">
        <thead>
          <tr className={tillPlate}>
            <th className="pb-2 text-left font-semibold">Mode</th>
            <th className="pb-2 text-right font-semibold">Bills</th>
            <th className="pb-2 text-right font-semibold">Taken</th>
            <th className="pb-2 text-right font-semibold">Refunded</th>
            <th className="pb-2 text-right font-semibold">Net</th>
          </tr>
        </thead>
        <tbody>
          {sales.modes.map((each) => (
            <tr key={each.mode} className="border-t border-border">
              <td className="py-3 pe-4">
                <p className="text-foreground">{modeLabel(each.mode)}</p>
                {/* The known limit, said where it bites. */}
                {each.mode === 'UNKNOWN' && (
                  <p className="text-sm text-muted-foreground">
                    Tickets sold before this till kept how they were paid
                  </p>
                )}
              </td>
              <td className="py-3 text-right align-top numeric">{each.bills}</td>
              <td className="py-3 text-right align-top numeric">{formatPaise(each.takenPaise)}</td>
              <td className="py-3 text-right align-top numeric">
                {formatPaise(each.refundedPaise)}
              </td>
              <td className="py-3 text-right align-top numeric">{formatPaise(each.netPaise)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-foreground font-semibold">
            <td className="py-3 pe-4 text-foreground">Total</td>
            <td className="py-3 text-right numeric">{sales.bills}</td>
            <td className="py-3 text-right numeric">{formatPaise(sales.takenPaise)}</td>
            <td className="py-3 text-right numeric">{formatPaise(sales.refundedPaise)}</td>
            <td className="py-3 text-right numeric">{formatPaise(sales.netPaise)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
